import axios from "axios";
import axiosRetry from "axios-retry";
import LocalStorageService from "./localStorageService";
import useAxiosResponse from "hooks/axiosResponse";
import { HttpStatus, PANEL_TOKEN_ZERO_TOKEN } from "constant";
import { stringify } from "qs";
import { pickBy } from "lodash";
import { APP_CONFIG } from "config/appConfig";
import keycloakService from "./keycloakService";
import { useAuthStore } from "store/useAuthStore";

const INVALID_REFRESH_TOKEN_MESSAGES = new Set([
    "Invalid Refresh Token",
    "Refresh Token has been revoked",
]);

const markShouldExpireSession = (error: any, expireSession: boolean) => {
    // error can be any type of error, so we need to check if it is an object
    if (error && typeof error === "object") {
        // expireSession is true if the session should be expired
        // Conditions for expireSession:
        // 1. No Refresh Token in storage
        // 2. No Access Token response when Refresh Token API called
        // 3. Refresh Token API return with BAD_REQUEST, UNAUTHORIZED, FORBIDDEN status code
        // 4. Refresh Token failed
        error.shouldExpireSession = expireSession;
    }
    return error;
};

const shouldExpireSession = (error: any) => {
    return !!error?.shouldExpireSession;
};

const refreshWithKeycloak = async (): Promise<string | null> => {
    try {
        const newToken = await keycloakService.forceRefreshToken();
        if (!newToken) {
            return null;
        }

        const { setTokens } = useAuthStore.getState();
        setTokens({
            AccessToken: newToken,
            RefreshToken: keycloakService.getRefreshToken(),
        });
        return newToken;
    } catch (error) {
        console.warn("Keycloak refresh failed, fallback to backend.", error);
        return null;
    }
};

const refreshWithBackend = async (): Promise<string | null> => {
    const { tokens, setTokens } = useAuthStore.getState();
    const { RefreshToken } = tokens || {};

    if (!RefreshToken) {
        throw markShouldExpireSession(
            new Error("No refresh token available"),
            true
        );
    }

    try {
        const response = await axios.post(
            `${APP_CONFIG.APP_DASHBOARD_API}/account/token`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${RefreshToken}`,
                },
            }
        );

        if ([HttpStatus.OK, HttpStatus.CREATED].includes(response.status)) {
            const AccessToken = response.data?.data?.AccessToken;

            if (!AccessToken) {
                throw markShouldExpireSession(
                    new Error("Refresh response missing AccessToken"),
                    true
                );
            }

            setTokens({
                ...tokens,
                AccessToken,
            });
            return AccessToken;
        }

        return null;
    } catch (error: any) {
        const description = error?.response?.data?.description;
        const statusCode = error?.response?.status;
        const isDefinitiveAuthFailure =
            INVALID_REFRESH_TOKEN_MESSAGES.has(description) ||
            [
                HttpStatus.BAD_REQUEST,
                HttpStatus.UNAUTHORIZED,
                HttpStatus.FORBIDDEN,
            ].includes(statusCode);

        throw markShouldExpireSession(error, isDefinitiveAuthFailure);
    }
};

export const refreshAuthToken = async (): Promise<string | null> => {
    // On-prem: try Keycloak first, then fallback to backend.
    if (APP_CONFIG.APP_IS_ONPREM) {
        const keycloakToken = await refreshWithKeycloak();
        if (keycloakToken) {
            return keycloakToken;
        }
    }

    // Cloud: backend only. On-prem: fallback branch.
    return refreshWithBackend();
};

const axiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
        // So far didn't found usage of text/plain, if its not necessary, feel free to remove it.
        Accept: "application/json,text/plain",
    },
    baseURL: APP_CONFIG.APP_DASHBOARD_API,
    paramsSerializer: (params) =>
        stringify(
            pickBy(
                params,
                (value) => value !== null && value !== undefined && value !== ""
            )
        ),
});


const shouldRetryRateLimit = (error: any) => {
    const statusCode = error?.response?.status;
    return statusCode === HttpStatus.TOO_MANY_REQUESTS;
};

axiosRetry(axiosInstance, {
    retries: 3,
    retryDelay: (retryCount) => {
        return retryCount * 1000;
    },
    retryCondition: shouldRetryRateLimit,
});

// Reference: https://gist.github.com/Godofbrowser/bf118322301af3fc334437c683887c5f
let isRefreshing = false;
let failedQueue: any = [];

/** Satisfy static analysis (reject with Error); keep Error instances (e.g. Axios) unchanged. */
const ensureRejectionError = (reason: unknown): Error =>
    reason instanceof Error ? reason : new Error(JSON.stringify(reason));

const processQueue = (error: any, token: string | null = null) => {
    for (const prom of failedQueue) {
        if (error) {
            prom.reject(ensureRejectionError(error));
        } else {
            prom.resolve(token);
        }
    }

    failedQueue = [];
};

const isMqttQuery = (url?: string) => {
    return url?.match(/(mqtt2tsdb|avg|sum|min|max)_/);
};

/**
 * USED for ON-PREM, Check if the request is for wallet or subscription
 * @param url - The URL of the request
 * @returns True if the request is for wallet or subscription, false otherwise
 */
const isWalletOrSubscription = (url?: string) => {
    return url?.match(/(wallet|subscription)/i);
};

/**
 * USED for ON-PREM, Get the mock response for wallet or subscription
 * @param config - The config of the request
 * @returns The mock response
 */
const getMockResponse = (config: any): any => {
    return {
        data: {
            data: null,
            message: "Fail",
        },
        status: 500,
        statusText: "Error",
        headers: {},
        config: config,
    };
};

const clearStorageExpireSession = () => {
    LocalStorageService.clearAll();
    globalThis.location.replace(
        `${APP_CONFIG.APP_PUBLIC_URL}/callback?session=expired`
    );
};

globalThis.window.panelQueryToken = {};

// add request interceptors
axiosInstance.interceptors.request.use(
    async (config) => {
        // USED for ON-PREM, Check if request is for wallet or subscription - use custom adapter
        if (APP_CONFIG.APP_IS_ONPREM && isWalletOrSubscription(config.url)) {
            config.adapter = (config) => {
                return Promise.resolve(getMockResponse(config));
            };
        }

        // Set the Authorization header with the AccessToken from local storage
        let token;
        const tokens = useAuthStore.getState().tokens;
        token = tokens?.AccessToken;

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(ensureRejectionError(error));
    }
);

const handleTokenRefresh = async (originalRequest: any) => {
    try {
        const newAccessToken = await refreshAuthToken();

        if (!newAccessToken) {
            throw markShouldExpireSession(
                new Error("Token refresh failed"),
                true
            );
        }

        // Update axios default header
        axiosInstance.defaults.headers.common[
            "Authorization"
        ] = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        // Retry the original request
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        try {
            const retryResponse = await axiosInstance(originalRequest);
            return retryResponse;
        } catch (retryError: any) {
            console.error(
                "Retry failed:",
                retryError.response?.status,
                retryError.response?.data
            );
            throw retryError;
        }
    } catch (error) {
        processQueue(error, null);
        if (shouldExpireSession(error)) {
            clearStorageExpireSession();
        }
        throw error;
    }
};

const isPanelQueryTokenPermissionDenied = (error: any, statusCode: number, url = "") => {
    return (
        error.response &&
        [HttpStatus.UNAUTHORIZED, HttpStatus.FORBIDDEN].includes(statusCode) &&
        isMqttQuery(url)
    );
};

const shouldRefreshToken = (
    error: any,
    statusCode: number,
    originalRequest: any,
    exceptPath: string | undefined,
    url = ""
) => {
    return (
        (error.response &&
            statusCode === HttpStatus.UNAUTHORIZED &&
            !originalRequest._retry &&
            !exceptPath) ||
        (statusCode === HttpStatus.BAD_REQUEST && url.includes("/account/info"))
    );
};

const queueRefreshRequest = (originalRequest: any) => {
    return new Promise((resolve, reject) => {
        failedQueue.push({
            resolve: (token: string) => {
                originalRequest.headers["Authorization"] = "Bearer " + token;
                resolve(axiosInstance(originalRequest));
            },
            reject: (err: any) => {
                reject(ensureRejectionError(err));
            },
        });
    });
};

//add response interceptors
axiosInstance.interceptors.response.use(
    (response) => useAxiosResponse(response),
    async (error) => {
        let originalRequest = error.config;
        const { url } = originalRequest;
        const exceptPath = ["/login", "/account/terminate"].find((u) =>
            url.includes(u)
        );
        const statusCode = error?.response?.status;

        if (
            isPanelQueryTokenPermissionDenied(error, statusCode, url) &&
            url.includes(PANEL_TOKEN_ZERO_TOKEN)
        ) {
            throw error;
        }

        if (statusCode === HttpStatus.FORBIDDEN && url.includes("/account/info")) {
            throw error;
        }

        if (shouldRefreshToken(error, statusCode, originalRequest, exceptPath, url)) {
            if (INVALID_REFRESH_TOKEN_MESSAGES.has(error?.response?.data?.description)) {
                clearStorageExpireSession();
            }

            if (isRefreshing) {
                // All requests during refresh are queued and retried with new token
                return queueRefreshRequest(originalRequest);
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                return await handleTokenRefresh(originalRequest);
            } catch (err: any) {
                processQueue(err, null);
                if (shouldExpireSession(err)) {
                    clearStorageExpireSession();
                }
                throw err;
            } finally {
                isRefreshing = false;
            }
        }
        throw error;
    }
);

export default axiosInstance;
